import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  AppBar,
  Toolbar,
  CircularProgress,
  Alert,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Chat as ChatIcon,
} from '@mui/icons-material'
import { Profile } from '@/types/profile'
import { getProfile, ProfileDetailResponse } from '@/services/profileService'
import { toRoomType } from '@/utils/roomType'
import { resolveOwnerName } from '@/utils/roomOwner'
import { ProfileDetailView } from '@/components/ProfileDetailView'
import { useAuth } from '@/contexts/AuthContext'
import { useState, useEffect } from 'react'

const getProfileColor = (profile: Profile) => {
  switch (profile.type) {
    case 'business': return 'primary.main'
    case 'dating': return '#ec4899'
    case 'job': return '#059669'
  }
}

export const ProfileDetailPage = () => {
  const { profileId } = useParams<{ profileId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const roomId = searchParams.get('roomId')
  const isOwnProfile = profileId === user?.userId
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchProfile = async () => {
      if (!profileId) return
      
      try {
        setLoading(true)
        setError(null)
        
        const profileData: ProfileDetailResponse = await getProfile(profileId, roomId ?? undefined)
        
        // APIレスポンスをProfile型にマッピング
        let mappedProfile: Profile

        // eventType はルーム内から開いた場合、バックエンドがそのルームの
        // カテゴリに合わせた値を返す
        const profileType = toRoomType(profileData.eventType)
        // 主催者はプロフィール未登録でもチャット相手として表示されるため、
        // 氏名が空なら既定の表示名に寄せる
        const ownerName = profileData.isRoomOwner
          ? resolveOwnerName(null, profileData.username)
          : ''

        // バックエンドで公開設定に基づきマスク済みなので、値をそのまま使用
        if (profileType === 'business') {
          mappedProfile = {
            id: profileData.userId,
            type: 'business' as const,
            pr: profileData.prText,
            photoUrl: profileData.bizPhotoUrl,
            createdAt: new Date(),
            name: profileData.bizName || ownerName,
            companyName: profileData.bizCompanyName || '',
            position: profileData.bizPosition || '',
            businessContent: profileData.bizContent || '',
            comment: profileData.bizComment || '',
            wantToMatch: profileData.bizTargetIndustry || '',
            notWantToMatch: profileData.bizExcludeIndustry || '',
            contactEmail: profileData.bizEmail || profileData.mailAddress || '',
            contactPhone: profileData.bizTel || '',
            subInfos: profileData.roomSubInfo,
          }
        } else if (profileType === 'dating') {
          mappedProfile = {
            id: profileData.userId,
            type: 'dating' as const,
            pr: profileData.prText,
            photoUrl: profileData.lovePhotoUrl,
            createdAt: new Date(),
            name: profileData.loveNickname || ownerName,
            age: profileData.loveAge || 0,
            occupation: profileData.loveJob || '',
            gender: profileData.loveGender as any,
            annualIncome: profileData.loveAnnualIncome || '',
            wantChildren: profileData.loveWantsKids || undefined,
            wantToMatch: profileData.loveTargetPerson || '',
            notWantToMatch: profileData.loveExcludePerson || '',
            contactEmail: profileData.loveContactInfo || profileData.mailAddress || '',
            contactPhone: profileData.loveTel || '',
            userNumber: profileData.userId,
            subInfos: profileData.roomSubInfo,
          }
        } else {
          mappedProfile = {
            id: profileData.userId,
            type: 'job' as const,
            pr: profileData.prText,
            photoUrl: profileData.careerPhotoUrl,
            createdAt: new Date(),
            name: profileData.careerName || ownerName,
            companyName: profileData.careerPrevCompany || '',
            lastPosition: profileData.careerLastPosition || '',
            education: profileData.careerEducationLevel || '',
            schoolName: profileData.careerSchoolName || '',
            age: profileData.careerAge || 0,
            gender: profileData.careerGender as any,
            comment: profileData.careerComment || '',
            wantToMatch: profileData.careerTargetCompany || '',
            notWantToMatch: profileData.careerExcludeConditions || '',
            contactEmail: profileData.careerEmail || profileData.mailAddress || '',
            contactPhone: profileData.careerTel || '',
            subInfos: profileData.roomSubInfo,
          }
        }

        setProfile({ ...mappedProfile, isRoomOwner: Boolean(profileData.isRoomOwner) })
      } catch (err) {
        console.error('Failed to fetch profile:', err)
        setError('プロフィールの取得に失敗しました')
      } finally {
        setLoading(false)
      }
    }

    fetchProfile()
  }, [profileId, roomId])

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !profile) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3 }}>
        <Alert severity="error">{error || 'プロフィールが見つかりません'}</Alert>
      </Box>
    )
  }

  const handleBack = () => {
    // SPA内の履歴がない場合(スマホで共有リンク等から直接開いた場合)、navigate(-1)では
    // アプリ外に遷移してしまい「戻る」が機能しないため、フォールバック先へ遷移する
    if (location.key !== 'default') {
      navigate(-1)
      return
    }
    if (roomId) {
      navigate(`/room/${roomId}`)
      return
    }
    navigate('/')
  }

  const handleChat = () => {
    if (roomId) {
      navigate(`/chat/${profile.id}?roomId=${roomId}`)
      return
    }
    navigate(`/chat/${profile.id}`)
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" sx={{ bgcolor: getProfileColor(profile) }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 1 }}>
            プロフィール
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ py: 3 }}>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <ProfileDetailView profile={profile} />
          </CardContent>
        </Card>

        {!isOwnProfile && (
          <Button
            variant="contained"
            startIcon={<ChatIcon />}
            fullWidth
            size="large"
            onClick={handleChat}
            sx={{
              mt: 3,
              bgcolor: getProfileColor(profile),
              '&:hover': { bgcolor: getProfileColor(profile), filter: 'brightness(0.9)' },
            }}
          >
            チャットする
          </Button>
        )}
      </Container>
    </Box>
  )
}
